// ANGEL FLIGHT — AudioBus: self-contained procedural WebAudio (NO assets).
//
// One AudioContext, lazily created and resumed on the first user gesture
// (browsers/iOS gate audio behind a gesture). A master GainNode → destination
// feeds two sub-gains — `sfx` and `music` — each scaled by GameSettings. Every
// sound is synthesised on the fly: the minigun roar is a layered noise-transient
// + body tone with round-robin pitch/gain variation so sustained fire reads as a
// snarling M134, not a metronome. Kills thump, vents hiss, hull hits crunch, and
// mission end plays a short rising/falling stinger; a near-silent low drone holds
// under the mission on the music bus.
//
// Lifecycle mirrors render/HudOverlay: bind(bus) subscribes to the per-mission
// event bus, unbind() detaches. Degrades silently when WebAudio is unavailable
// or disallowed (headless tests, blocked contexts) — never throws. Every
// scheduled node is stopped + disconnected on end, so sustained fire never leaks.

import type { TypedEmitter } from '../core/EventBus';
import type { GameSettings, WeaponId } from '../core/types';
import { makeNoiseBuffer } from './noise';

/** The lazily-built audio graph; null until a successful unlock(). */
interface Graph {
  ctx: AudioContext;
  master: GainNode;
  sfx: GainNode;
  music: GainNode;
  noise: AudioBuffer;
}

/** webkit-prefixed AudioContext for older iOS Safari. */
type AudioContextCtor = typeof AudioContext;
function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

const MIN_GAIN = 0.0001; // exponential ramps can't reach 0

export class AudioBus {
  private graph: Graph | null = null;
  private bus: TypedEmitter | null = null;
  /** Last-known volumes; applied to the graph the moment it exists. */
  private vol: Pick<GameSettings, 'master' | 'sfx' | 'music'> = {
    master: 1,
    sfx: 1,
    music: 1,
  };
  /** Round-robin index → per-shot pitch/gain variation (no metronome). */
  private shotTick = 0;
  /** Mission drone source (music bed); stopped on unbind. */
  private drone: { osc: OscillatorNode; gain: GainNode } | null = null;
  private droneActive = false;
  /** Rotor-wash loop + dusk wind ambience (the helicopter identity bed). */
  private rotor: { srcs: AudioScheduledSourceNode[]; gain: GainNode } | null = null;
  /** Gun spin-up whine: a continuous osc whose pitch/level track gunner.spinup. */
  private whine: { osc: OscillatorNode; gain: GainNode } | null = null;
  /** Menu theme loop (a quiet arpeggio pad on the music bus). */
  private menuTheme: { srcs: AudioScheduledSourceNode[]; gain: GainNode; timer: number } | null = null;
  private menuActive = false;

  // ── unlock / context ──────────────────────────────────────────────────────

  /** Create (once) and resume the AudioContext. Call from a user gesture. */
  unlock(): void {
    if (!this.graph) {
      const Ctor = getAudioContextCtor();
      if (!Ctor) return; // no WebAudio → silent
      try {
        const ctx = new Ctor();
        const master = ctx.createGain();
        const sfx = ctx.createGain();
        const music = ctx.createGain();
        master.connect(ctx.destination);
        sfx.connect(master);
        music.connect(master);
        this.graph = { ctx, master, sfx, music, noise: makeNoiseBuffer(ctx, 1) };
        this.applyVolumes();
      } catch {
        this.graph = null; // degrade silently
        return;
      }
    }
    // Resume — may reject if still gestureless; ignore (next gesture retries).
    void this.graph.ctx.resume().catch(() => {});
    // If a mission is already bound, (re)start the beds now that we have audio.
    if (this.droneActive) {
      this.startDrone();
      this.startRotor();
      this.startWhine();
    }
    if (this.menuActive) this.startMenuTheme();
  }

  // ── volumes ─────────────────────────────────────────────────────────────────

  setVolumes(settings: GameSettings): void {
    this.vol = { master: settings.master, sfx: settings.sfx, music: settings.music };
    this.applyVolumes();
  }

  private applyVolumes(): void {
    const g = this.graph;
    if (!g) return;
    const clamp = (v: number): number => Math.max(0, Math.min(1, v));
    g.master.gain.value = clamp(this.vol.master);
    g.sfx.gain.value = clamp(this.vol.sfx);
    // Keep the music bed well under SFX so the drone never masks the action.
    g.music.gain.value = clamp(this.vol.music) * 0.6;
  }

  // ── bind / unbind (mirrors HudOverlay) ──────────────────────────────────────

  bind(bus: TypedEmitter): void {
    this.unbind();
    this.stopMenuTheme();
    this.menuActive = false;
    this.bus = bus;
    bus.on('weapon:fired', this.onFired);
    bus.on('weapon:spinup', this.onSpinup);
    bus.on('weapon:dry', this.onDry);
    bus.on('weapon:reloadStart', this.onReload);
    bus.on('enemy:killed', this.onKilled);
    bus.on('hull:damaged', this.onHull);
    bus.on('friendly:warning', this.onFriendlyWarning);
    bus.on('flare:deployed', this.onFlare);
    bus.on('comms:line', this.onComms);
    bus.on('mission:cleared', this.onCleared);
    bus.on('mission:failed', this.onFailed);
    // Arm the beds; they actually spin up once audio is unlocked.
    this.droneActive = true;
    this.startDrone();
    this.startRotor();
    this.startWhine();
  }

  unbind(): void {
    if (this.bus) {
      this.bus.off('weapon:fired', this.onFired);
      this.bus.off('weapon:spinup', this.onSpinup);
      this.bus.off('weapon:dry', this.onDry);
      this.bus.off('weapon:reloadStart', this.onReload);
      this.bus.off('enemy:killed', this.onKilled);
      this.bus.off('hull:damaged', this.onHull);
      this.bus.off('friendly:warning', this.onFriendlyWarning);
      this.bus.off('flare:deployed', this.onFlare);
      this.bus.off('comms:line', this.onComms);
      this.bus.off('mission:cleared', this.onCleared);
      this.bus.off('mission:failed', this.onFailed);
      this.bus = null;
    }
    this.droneActive = false;
    this.stopDrone();
    this.stopRotor();
    this.stopWhine();
  }

  // ── event handlers (arrow props → stable identity for off()) ────────────────

  private readonly onFired = (p: { weapon: WeaponId }): void => this.playShot(p.weapon);
  private readonly onSpinup = (p: { t: number }): void => this.setWhine(p.t);
  private readonly onDry = (): void => this.playDry();
  private readonly onReload = (): void => this.playVent();
  private readonly onKilled = (): void => this.playKill();
  private readonly onHull = (): void => this.playHull();
  private readonly onFriendlyWarning = (): void => this.playKlaxon();
  private readonly onFlare = (): void => this.playFlare();
  private readonly onComms = (p: { urgent: boolean }): void => this.playSquelch(p.urgent);
  private readonly onCleared = (): void => this.playStinger(true);
  private readonly onFailed = (): void => this.playStinger(false);

  // ── synthesis ────────────────────────────────────────────────────────────────

  /** Per-weapon synth voicing — all four guns no longer share one bark.
   *  Tunables: lowpass start/end, body wave/pitch, levels, tail length. */
  private static readonly SHOT_VOICE: Readonly<
    Record<WeaponId, {
      lpFrom: number;
      lpTo: number;
      bodyType: OscillatorType;
      bodyFrom: number;
      bodyTo: number;
      noiseLvl: number;
      bodyLvl: number;
      tail: number;
    }>
  > = Object.freeze({
    // The M134 snarl — the established crack+chug.
    minigun: { lpFrom: 2600, lpTo: 420, bodyType: 'square', bodyFrom: 150, bodyTo: 70, noiseLvl: 0.5, bodyLvl: 0.22, tail: 0.06 },
    // 30mm: slower cadence, much deeper, longer boom.
    autocannon: { lpFrom: 1400, lpTo: 180, bodyType: 'square', bodyFrom: 95, bodyTo: 38, noiseLvl: 0.62, bodyLvl: 0.4, tail: 0.14 },
    // Railgun: an electric crack — bright noise + a high falling zap.
    railgun: { lpFrom: 7000, lpTo: 1400, bodyType: 'sawtooth', bodyFrom: 1900, bodyTo: 220, noiseLvl: 0.45, bodyLvl: 0.18, tail: 0.18 },
    // Incinerator: a soft breathy whoosh, almost no mechanical body.
    flamer: { lpFrom: 900, lpTo: 500, bodyType: 'sine', bodyFrom: 90, bodyTo: 60, noiseLvl: 0.34, bodyLvl: 0.06, tail: 0.16 },
  });

  /**
   * One round of the active weapon: a noise-burst transient through a snappy
   * lowpass + a short pitched body tone, voiced per weapon (SHOT_VOICE).
   * Round-robins pitch/gain so sustained fire lives. ~5 nodes, all stopped +
   * disconnected on end → safe at 15-20 shots/sec.
   */
  private playShot(weapon: WeaponId = 'minigun'): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    const v = AudioBus.SHOT_VOICE[weapon] ?? AudioBus.SHOT_VOICE.minigun;
    const ctx = g.ctx;
    const t = ctx.currentTime;
    const tick = this.shotTick++;
    // Round-robin variation: small per-shot pitch & level wobble.
    const pitchVar = 1 + ((tick % 7) - 3) * 0.018; // ±~5%
    const levelVar = 0.85 + ((tick * 0.37) % 1) * 0.3; // 0.85..1.15

    // — transient: short white-noise crack through a fast-decaying lowpass —
    const src = ctx.createBufferSource();
    src.buffer = g.noise;
    src.playbackRate.value = pitchVar;
    const off = Math.random() * Math.max(0, g.noise.duration - 0.1);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(v.lpFrom * pitchVar, t);
    lp.frequency.exponentialRampToValueAtTime(v.lpTo, t + v.tail * 0.85);
    lp.Q.value = 0.9;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(v.noiseLvl * levelVar, t);
    ng.gain.exponentialRampToValueAtTime(MIN_GAIN, t + v.tail);
    src.connect(lp).connect(ng).connect(g.sfx);

    // — body: a pitched thump/zap for the weapon's mechanical character —
    const body = ctx.createOscillator();
    body.type = v.bodyType;
    body.frequency.setValueAtTime(v.bodyFrom * pitchVar, t);
    body.frequency.exponentialRampToValueAtTime(v.bodyTo, t + v.tail * 0.85);
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(v.bodyLvl * levelVar, t);
    bg.gain.exponentialRampToValueAtTime(MIN_GAIN, t + v.tail * 0.95);
    body.connect(bg).connect(g.sfx);

    const stop = t + v.tail + 0.02;
    src.start(t, off);
    src.stop(stop);
    body.start(t);
    body.stop(stop);
    const cleanup = (): void => {
      src.disconnect();
      lp.disconnect();
      ng.disconnect();
      body.disconnect();
      bg.disconnect();
    };
    src.onended = cleanup;
    body.onended = null; // src ends last; one cleanup is enough
  }

  /** Crew radio squelch: a short bandpassed (300-3000Hz) noise blip with a
   *  gate click on each end — the comms line's voice. Urgent lines bark a
   *  touch louder. ~4 nodes, self-cleaning. */
  private playSquelch(urgent: boolean): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    const ctx = g.ctx;
    const t = ctx.currentTime;
    const lvl = urgent ? 0.3 : 0.2;

    // The radio voice band: noise through a 300-3000Hz bandpass.
    const src = ctx.createBufferSource();
    src.buffer = g.noise;
    src.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1400; // centre of the 300-3000 voice band
    bp.Q.value = 0.45; // wide — covers the whole band
    const sg = ctx.createGain();
    // Gate ON click (instant attack), a short crackle hold, gate OFF.
    sg.gain.setValueAtTime(lvl, t);
    sg.gain.setValueAtTime(lvl * 0.55, t + 0.025);
    sg.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.14);
    src.connect(bp).connect(sg).connect(g.sfx);

    // The gate click itself — a tiny high tick at open + close.
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = 2400;
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.08, t);
    og.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.02);
    og.gain.setValueAtTime(0.06, t + 0.13);
    og.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.16);
    osc.connect(og).connect(g.sfx);

    const stop = t + 0.18;
    src.start(t, Math.random() * 0.5);
    src.stop(stop);
    osc.start(t);
    osc.stop(stop);
    src.onended = (): void => {
      src.disconnect();
      bp.disconnect();
      sg.disconnect();
      osc.disconnect();
      og.disconnect();
    };
  }

  /** UI sound for menu/touch interactions: click / confirm / back. */
  playUi(kind: 'click' | 'confirm' | 'back'): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    const ctx = g.ctx;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    const freq = kind === 'confirm' ? 660 : kind === 'back' ? 320 : 480;
    osc.frequency.setValueAtTime(freq, t);
    if (kind === 'confirm') osc.frequency.exponentialRampToValueAtTime(880, t + 0.07);
    else if (kind === 'back') osc.frequency.exponentialRampToValueAtTime(220, t + 0.07);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.18, t);
    og.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.09);
    osc.connect(og).connect(g.sfx);
    osc.start(t);
    osc.stop(t + 0.1);
    osc.onended = (): void => {
      osc.disconnect();
      og.disconnect();
    };
  }

  /** Dry-fire/mag-out: a short mechanical CLICK (the cue the gun ran empty —
   *  distinct from the vent hiss that follows). Two tiny ticks: a high snap +
   *  a lower body knock. ~4 nodes, self-cleaning. */
  private playDry(): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    const ctx = g.ctx;
    const t = ctx.currentTime;

    // High metallic snap — bandpassed noise blip.
    const src = ctx.createBufferSource();
    src.buffer = g.noise;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 3400;
    bp.Q.value = 3.5;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.5, t);
    ng.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.035);
    src.connect(bp).connect(ng).connect(g.sfx);

    // Low knock under it — the bolt falling on an empty chamber.
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(420, t);
    osc.frequency.exponentialRampToValueAtTime(160, t + 0.03);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.16, t);
    og.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.04);
    osc.connect(og).connect(g.sfx);

    const stop = t + 0.05;
    src.start(t, Math.random() * 0.5);
    src.stop(stop);
    osc.start(t);
    osc.stop(stop);
    src.onended = (): void => {
      src.disconnect();
      bp.disconnect();
      ng.disconnect();
      osc.disconnect();
      og.disconnect();
    };
  }

  /** Kill thump: a low filtered sine/noise thud. */
  private playKill(): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    const ctx = g.ctx;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(48, t + 0.18);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, t);
    og.gain.exponentialRampToValueAtTime(0.5, t + 0.01);
    og.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.22);
    osc.connect(og).connect(g.sfx);

    const src = ctx.createBufferSource();
    src.buffer = g.noise;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 320;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.25, t);
    ng.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.12);
    src.connect(lp).connect(ng).connect(g.sfx);

    const stop = t + 0.24;
    osc.start(t);
    osc.stop(stop);
    src.start(t, Math.random() * 0.5);
    src.stop(t + 0.14);
    osc.onended = (): void => {
      osc.disconnect();
      og.disconnect();
      src.disconnect();
      lp.disconnect();
      ng.disconnect();
    };
  }

  /** Reload/vent: a brief steam hiss with a falling whine. */
  private playVent(): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    const ctx = g.ctx;
    const t = ctx.currentTime;

    // Hiss — bandpassed noise that swells then fades.
    const src = ctx.createBufferSource();
    src.buffer = g.noise;
    src.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(2200, t);
    bp.frequency.exponentialRampToValueAtTime(1100, t + 0.5);
    bp.Q.value = 0.7;
    const hg = ctx.createGain();
    hg.gain.setValueAtTime(MIN_GAIN, t);
    hg.gain.exponentialRampToValueAtTime(0.18, t + 0.06);
    hg.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.5);
    src.connect(bp).connect(hg).connect(g.sfx);

    // Whine — a thin falling tone under the hiss.
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(900, t);
    osc.frequency.exponentialRampToValueAtTime(280, t + 0.5);
    const wg = ctx.createGain();
    wg.gain.setValueAtTime(0.05, t);
    wg.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.5);
    osc.connect(wg).connect(g.sfx);

    const stop = t + 0.52;
    src.start(t, Math.random() * 0.5);
    src.stop(stop);
    osc.start(t);
    osc.stop(stop);
    src.onended = (): void => {
      src.disconnect();
      bp.disconnect();
      hg.disconnect();
      osc.disconnect();
      wg.disconnect();
    };
  }

  /** Hull hit: a harsh low impact — detuned saw crunch + noise. */
  private playHull(): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    const ctx = g.ctx;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(140, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.25);
    const dist = ctx.createWaveShaper();
    dist.curve = HARD_CLIP;
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.5, t);
    og.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.3);
    osc.connect(dist).connect(og).connect(g.sfx);

    const src = ctx.createBufferSource();
    src.buffer = g.noise;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 800;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.35, t);
    ng.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.15);
    src.connect(lp).connect(ng).connect(g.sfx);

    const stop = t + 0.32;
    osc.start(t);
    osc.stop(stop);
    src.start(t, Math.random() * 0.5);
    src.stop(t + 0.16);
    osc.onended = (): void => {
      osc.disconnect();
      dist.disconnect();
      og.disconnect();
      src.disconnect();
      lp.disconnect();
      ng.disconnect();
    };
  }

  /** CHECK-FIRE warning klaxon (penultimate friendly-fire strike): two urgent
   *  two-tone warble pulses — unmistakably an alarm, distinct from every combat
   *  sound. ~6 nodes, all stopped + disconnected on end. */
  private playKlaxon(): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    const ctx = g.ctx;
    const t0 = ctx.currentTime;
    const pulseLen = 0.22;
    const gap = 0.08;
    for (let i = 0; i < 2; i++) {
      const t = t0 + i * (pulseLen + gap);
      // Two detuned squares a tritone apart = the classic harsh alarm bark.
      const hi = ctx.createOscillator();
      hi.type = 'square';
      hi.frequency.setValueAtTime(880, t);
      hi.frequency.exponentialRampToValueAtTime(620, t + pulseLen);
      const lo = ctx.createOscillator();
      lo.type = 'square';
      lo.frequency.setValueAtTime(622, t);
      lo.frequency.exponentialRampToValueAtTime(440, t + pulseLen);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(MIN_GAIN, t);
      gain.gain.exponentialRampToValueAtTime(0.4, t + 0.015);
      gain.gain.setValueAtTime(0.4, t + pulseLen - 0.04);
      gain.gain.exponentialRampToValueAtTime(MIN_GAIN, t + pulseLen);
      hi.connect(gain);
      lo.connect(gain);
      gain.connect(g.sfx);
      const stop = t + pulseLen + 0.02;
      hi.start(t);
      hi.stop(stop);
      lo.start(t);
      lo.stop(stop);
      hi.onended = (): void => {
        hi.disconnect();
        lo.disconnect();
        gain.disconnect();
      };
    }
  }

  /** Countermeasure flares: two quick launcher THUNKS + a bright falling
   *  sparkle hiss — unmistakably "flares out", distinct from gunfire. */
  private playFlare(): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    const ctx = g.ctx;
    const t0 = ctx.currentTime;
    for (let i = 0; i < 2; i++) {
      const t = t0 + i * 0.09;
      // Launcher thunk — a low pitched knock.
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(220, t);
      osc.frequency.exponentialRampToValueAtTime(70, t + 0.08);
      const og = ctx.createGain();
      og.gain.setValueAtTime(0.4, t);
      og.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.1);
      osc.connect(og).connect(g.sfx);
      osc.start(t);
      osc.stop(t + 0.12);
      osc.onended = (): void => {
        osc.disconnect();
        og.disconnect();
      };
    }
    // Burning-flare sparkle: a bright bandpassed hiss that falls away.
    const src = ctx.createBufferSource();
    src.buffer = g.noise;
    src.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(5200, t0 + 0.06);
    bp.frequency.exponentialRampToValueAtTime(1800, t0 + 0.8);
    bp.Q.value = 0.8;
    const hg = ctx.createGain();
    hg.gain.setValueAtTime(MIN_GAIN, t0 + 0.06);
    hg.gain.exponentialRampToValueAtTime(0.16, t0 + 0.12);
    hg.gain.exponentialRampToValueAtTime(MIN_GAIN, t0 + 0.85);
    src.connect(bp).connect(hg).connect(g.sfx);
    src.start(t0 + 0.06, Math.random() * 0.5);
    src.stop(t0 + 0.9);
    src.onended = (): void => {
      src.disconnect();
      bp.disconnect();
      hg.disconnect();
    };
  }

  /** Mission stinger: 3 notes, rising for cleared, falling for failed. */
  private playStinger(cleared: boolean): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    const ctx = g.ctx;
    const t0 = ctx.currentTime;
    const notes = cleared ? [392, 523.25, 659.25] : [392, 311.13, 196];
    const step = 0.16;
    for (let i = 0; i < notes.length; i++) {
      const freq = notes[i];
      if (freq === undefined) continue;
      const t = t0 + i * step;
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      const og = ctx.createGain();
      og.gain.setValueAtTime(MIN_GAIN, t);
      og.gain.exponentialRampToValueAtTime(0.32, t + 0.02);
      og.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.28);
      osc.connect(og).connect(g.music);
      osc.start(t);
      osc.stop(t + 0.3);
      osc.onended = (): void => {
        osc.disconnect();
        og.disconnect();
      };
    }
  }

  // ── ambience drone (music bus) ──────────────────────────────────────────────

  /** A near-silent low drone bed held under the mission. Idempotent. */
  private startDrone(): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running' || this.drone) return;
    const ctx = g.ctx;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = 41.2; // low E1 rumble
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 140;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(MIN_GAIN, t);
    gain.gain.linearRampToValueAtTime(0.14, t + 2); // slow swell-in
    osc.connect(lp).connect(gain).connect(g.music);
    osc.start(t);
    // Keep the filter referenced via the closure so it isn't GC'd mid-play.
    this.drone = { osc, gain };
    osc.onended = (): void => {
      osc.disconnect();
      lp.disconnect();
      gain.disconnect();
    };
  }

  /** Rotor wash + dusk wind — the helicopter identity bed, held under the
   *  mission on the music bus. Voiced as a PERCUSSIVE blade beat, not a noise
   *  floor: the old mix (bright 260Hz noise at a shallow ±0.45 chop + a loud
   *  wide 700Hz "wind" band) never fully gated and read as CONSTANT STATIC —
   *  the audited "annoying static sound that never stops". Now the noise is
   *  filtered much deeper, the chop gates ~fully at the blade rate (a real
   *  WHOP-WHOP with silence between beats), a small sub thump pulses with it,
   *  and the wind is a quiet low whisper instead of broadband hiss. Idempotent. */
  private startRotor(): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running' || this.rotor) return;
    const ctx = g.ctx;
    const t = ctx.currentTime;
    const out = ctx.createGain();
    out.gain.setValueAtTime(MIN_GAIN, t);
    out.gain.linearRampToValueAtTime(0.15, t + 1.5); // swell in with the drone
    out.connect(g.music);

    // — rotor: deep-filtered noise gated ~fully at the blade-pass rate —
    const noise = ctx.createBufferSource();
    noise.buffer = g.noise;
    noise.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 170; // deep thud band — no hissy top end
    lp.Q.value = 1.1; // slight resonant knock at the cutoff
    const chop = ctx.createGain();
    chop.gain.value = 0.5; // LFO swings ±0.5 → gates to ~zero between beats
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 10.8; // heavy twin-blade WHOP
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 0.5;
    lfo.connect(lfoDepth).connect(chop.gain);
    noise.connect(lp).connect(chop).connect(out);

    // — sub thump: a quiet low sine pulsed by the SAME chop phase, giving each
    //   blade beat a body you feel more than hear —
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = 54;
    const subChop = ctx.createGain();
    subChop.gain.value = 0.1; // pulsed ±0.1 by the shared LFO
    const subDepth = ctx.createGain();
    subDepth.gain.value = 0.1;
    lfo.connect(subDepth).connect(subChop.gain);
    sub.connect(subChop).connect(out);

    // — dusk wind: a QUIET low murmur that gusts slowly (the open door at
    //   altitude) — kept dark + soft so it never reads as static —
    const wind = ctx.createBufferSource();
    wind.buffer = g.noise;
    wind.loop = true;
    const wbp = ctx.createBiquadFilter();
    wbp.type = 'bandpass';
    wbp.frequency.value = 420;
    wbp.Q.value = 0.6;
    const wg = ctx.createGain();
    wg.gain.value = 0.05;
    const wLfo = ctx.createOscillator();
    wLfo.type = 'sine';
    wLfo.frequency.value = 0.13; // slow gusting drift
    const wDepth = ctx.createGain();
    wDepth.gain.value = 0.025;
    wLfo.connect(wDepth).connect(wg.gain);
    wind.connect(wbp).connect(wg).connect(out);

    noise.start(t);
    wind.start(t, Math.min(0.5, g.noise.duration / 2));
    sub.start(t);
    lfo.start(t);
    wLfo.start(t);
    this.rotor = { srcs: [noise, wind, sub, lfo, wLfo], gain: out };
  }

  private stopRotor(): void {
    const r = this.rotor;
    if (!r) return;
    this.rotor = null;
    const g = this.graph;
    if (!g) return;
    const t = g.ctx.currentTime;
    try {
      r.gain.gain.cancelScheduledValues(t);
      r.gain.gain.setValueAtTime(Math.max(MIN_GAIN, r.gain.gain.value), t);
      r.gain.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.4);
      for (const s of r.srcs) s.stop(t + 0.45);
      window.setTimeout(() => r.gain.disconnect(), 600);
    } catch {
      for (const s of r.srcs) {
        try {
          s.disconnect();
        } catch {
          /* already gone */
        }
      }
      r.gain.disconnect();
    }
  }

  /** The gun whine: a continuous quiet oscillator whose pitch + level track
   *  gunner.spinup (the weapon:spinup event fires every frame in-mission).
   *  Voiced as a LOW-PASSED TRIANGLE with a modest pitch ceiling — the old raw
   *  sawtooth at 760Hz was the audited "screeching sound" (ear-piercing buzz).
   *  Silent at t=0, a soft motor spin toward full fire. Idempotent. */
  private startWhine(): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running' || this.whine) return;
    const ctx = g.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = 140;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 850; // shave the upper harmonics — motor hum, not a shriek
    lp.Q.value = 0.5;
    const gain = ctx.createGain();
    gain.gain.value = 0; // setWhine drives it
    osc.connect(lp).connect(gain).connect(g.sfx);
    osc.start(ctx.currentTime);
    this.whine = { osc, gain };
  }

  /** Track the spin-up parameter (0..1) — smoothed so it never zippers. */
  private setWhine(t01: number): void {
    const w = this.whine;
    const g = this.graph;
    if (!w || !g) return;
    const t = g.ctx.currentTime;
    const k = Math.max(0, Math.min(1, t01));
    // Pitch climbs 140→460Hz; level fades in above ~15% spin, capped QUIET.
    w.osc.frequency.setTargetAtTime(140 + 320 * k, t, 0.06);
    const lvl = k < 0.15 ? 0 : (k - 0.15) * 0.055;
    w.gain.gain.setTargetAtTime(lvl, t, 0.08);
  }

  private stopWhine(): void {
    const w = this.whine;
    if (!w) return;
    this.whine = null;
    const g = this.graph;
    if (!g) return;
    const t = g.ctx.currentTime;
    try {
      w.gain.gain.cancelScheduledValues(t);
      w.gain.gain.setTargetAtTime(0, t, 0.05);
      w.osc.stop(t + 0.3);
      w.osc.onended = (): void => {
        w.osc.disconnect();
        w.gain.disconnect();
      };
    } catch {
      w.osc.disconnect();
      w.gain.disconnect();
    }
  }

  // ── menu theme ──────────────────────────────────────────────────────────────

  /** A quiet looping pad behind the menus: a slow minor arpeggio over a low
   *  root, all on the music bus. Armed by App.showMenu; stops on deploy. */
  startMenuTheme(): void {
    this.menuActive = true;
    const g = this.graph;
    if (!g || g.ctx.state !== 'running' || this.menuTheme) return;
    const ctx = g.ctx;
    const t = ctx.currentTime;
    const out = ctx.createGain();
    out.gain.setValueAtTime(MIN_GAIN, t);
    out.gain.linearRampToValueAtTime(0.16, t + 1.2);
    out.connect(g.music);

    // Low root pad.
    const root = ctx.createOscillator();
    root.type = 'triangle';
    root.frequency.value = 98; // G2
    const rg = ctx.createGain();
    rg.gain.value = 0.5;
    root.connect(rg).connect(out);
    root.start(t);

    // A slow arpeggio voiced one note at a time on an interval timer (DOM
    // timer — menu-only, torn down with the theme).
    const seq = [196, 233.08, 293.66, 233.08]; // G3 · Bb3 · D4 · Bb3
    let step = 0;
    const voice = ctx.createOscillator();
    voice.type = 'sine';
    voice.frequency.value = seq[0] ?? 196;
    const vg = ctx.createGain();
    vg.gain.value = 0;
    voice.connect(vg).connect(out);
    voice.start(t);
    const timer = window.setInterval(() => {
      const gg = this.graph;
      if (!gg) return;
      const now = gg.ctx.currentTime;
      const f = seq[step % seq.length] ?? 196;
      step++;
      voice.frequency.setTargetAtTime(f, now, 0.02);
      vg.gain.cancelScheduledValues(now);
      vg.gain.setValueAtTime(0.0001, now);
      vg.gain.exponentialRampToValueAtTime(0.35, now + 0.12);
      vg.gain.exponentialRampToValueAtTime(0.0001, now + 1.5);
    }, 1600);

    this.menuTheme = { srcs: [root, voice], gain: out, timer };
  }

  stopMenuTheme(): void {
    this.menuActive = false;
    const m = this.menuTheme;
    if (!m) return;
    this.menuTheme = null;
    window.clearInterval(m.timer);
    const g = this.graph;
    if (!g) return;
    const t = g.ctx.currentTime;
    try {
      m.gain.gain.cancelScheduledValues(t);
      m.gain.gain.setValueAtTime(Math.max(MIN_GAIN, m.gain.gain.value), t);
      m.gain.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.4);
      for (const s of m.srcs) s.stop(t + 0.45);
      window.setTimeout(() => m.gain.disconnect(), 600);
    } catch {
      for (const s of m.srcs) {
        try {
          s.disconnect();
        } catch {
          /* already gone */
        }
      }
      m.gain.disconnect();
    }
  }

  private stopDrone(): void {
    const d = this.drone;
    if (!d) return;
    this.drone = null;
    const g = this.graph;
    if (!g) return;
    const t = g.ctx.currentTime;
    try {
      d.gain.gain.cancelScheduledValues(t);
      d.gain.gain.setValueAtTime(Math.max(MIN_GAIN, d.gain.gain.value), t);
      d.gain.gain.exponentialRampToValueAtTime(MIN_GAIN, t + 0.3);
      d.osc.stop(t + 0.32); // onended disconnects the whole chain
    } catch {
      // already stopped / context closed — disconnect best-effort
      d.osc.disconnect();
      d.gain.disconnect();
    }
  }

  // ── teardown ────────────────────────────────────────────────────────────────

  destroy(): void {
    this.unbind();
    this.stopMenuTheme();
    const g = this.graph;
    this.graph = null;
    if (!g) return;
    try {
      void g.ctx.close();
    } catch {
      // ignore — best effort
    }
  }
}

/** A static hard-clip curve for the hull-hit waveshaper (built once). Backed by
 *  an explicit ArrayBuffer so its type matches WaveShaperNode.curve exactly. */
const HARD_CLIP = ((): Float32Array<ArrayBuffer> => {
  const n = 1024;
  const curve = new Float32Array(new ArrayBuffer(n * Float32Array.BYTES_PER_ELEMENT));
  const k = 8; // drive
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(k * x);
  }
  return curve;
})();
